import { getOptionalStringValue } from '@pba/react-network';
import { type NodeUser, type Pagination, createNodeUser } from 'models';
import { type UseQueryOptions, useQuery } from 'query';
import type { PaginationData } from 'common/services';
import type { ApiClientResponseError, ResponseErrorDataObject } from 'common/services/api';
import { getNodeUsers } from '../services';
import { getNodeUsersQueryKey } from './keys';

export type NodeUsersQueryFilter = {
  $search?: string;
  scope: 'admin' | 'web';
};

type NodeUsersParams = {
  filter: NodeUsersQueryFilter;
  options?: Omit<
    UseQueryOptions<
      PaginationData<NodeUser>,
      ApiClientResponseError<ResponseErrorDataObject>,
      PaginationData<NodeUser>
    >,
    'queryFn' | 'queryKey'
  >;
  pagination?: Partial<Pagination>;
};

export const useGetNodeUsers = ({ filter, options, pagination }: NodeUsersParams) =>
  useQuery<PaginationData<NodeUser>, ApiClientResponseError<ResponseErrorDataObject>, PaginationData<NodeUser>>({
    async queryFn() {
      const pageSize = pagination?.pageSize ?? 10;
      const response = await getNodeUsers({
        config: {
          params: {
            $limit: pageSize,
            $search: getOptionalStringValue(filter.$search),
            $skip: ((pagination?.position ?? 1) - 1) * pageSize,
            scope: filter.scope,
          },
        },
      });

      return {
        data: response.data.data.map((item) => createNodeUser(item)),
        limit: response.data.limit,
        skip: response.data.skip,
        total: response.data.total,
      };
    },
    queryKey: getNodeUsersQueryKey({ filter, pagination }),
    ...options,
  });
